import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createDocumentType, updateDocumentType, fetchDocumentTypes } from './api';
import { DocumentTypeRead } from './types';
import { ArrowBackOutlined as BackIcon, CategoryOutlined as TypeIcon } from '@mui/icons-material';

// Импортируем кастомный чекбокс
import { CustomCheckbox } from '../../components/ui/CustomCheckbox';

const DocumentTypeForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(isEditing);

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Инициализация данных при редактировании
  useEffect(() => {
    if (isEditing) {
      // 1. Пытаемся получить данные из стейта роутера (переданы из списка)
      if (location.state?.type) {
        const t = location.state.type as DocumentTypeRead;
        populateForm(t);
        setInitializing(false);
      } else {
        // 2. Если пришли по прямой ссылке, подгружаем список и ищем нужный (т.к. нет fetchDocumentTypeById)
        fetchDocumentTypes()
          .then(types => {
            const t = types.find(type => String(type.id) === id);
            if (t) populateForm(t);
            else alert('Тип документа не найден');
          })
          .catch(err => alert('Ошибка загрузки данных'))
          .finally(() => setInitializing(false));
      }
    }
  }, [id, isEditing, location.state]);

  const populateForm = (t: DocumentTypeRead) => {
    setName(t.name);
    setCode(t.code);
    setDescription(t.description || '');
    setIsActive(t.is_active);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.toUpperCase();
    val = val.replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
    setCode(val);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !code) {
      alert('Название и Код обязательны для заполнения');
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await updateDocumentType(Number(id), {
          name,
          description,
          is_active: isActive
        });
      } else {
        await createDocumentType({ name, code, description, is_active: isActive });
      }
      navigate('/documents/types');
    } catch (error: any) {
      alert(`Ошибка: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (initializing) {
    return <div className="p-8 text-center text-gray-500">Загрузка данных...</div>;
  }

  return (
    <div className="w-full bg-white pb-10">
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">

        <div className="max-w-3xl mx-auto space-y-6">
          <button
            onClick={() => navigate('/documents/types')}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors font-medium"
          >
            <BackIcon fontSize="small" /> Назад к списку
          </button>

          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <TypeIcon className="text-[#1E61AF]" />
              {isEditing ? 'Редактирование типа документа' : 'Новый тип документа'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название типа <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
                  placeholder="Например: Договор об обучении"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Системный код <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  disabled={isEditing}
                  value={code}
                  onChange={handleCodeChange}
                  className={`w-full border rounded-lg px-4 py-2 outline-none font-mono text-sm transition-all ${
                    isEditing
                      ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
                      : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                  placeholder="CONTRACT_TYPE"
                />
                {!isEditing && <p className="text-xs text-gray-500 mt-1.5">Используется в программном коде. Допустимы только латинские буквы, цифры и символ подчеркивания (_).</p>}
                {isEditing && <p className="text-xs text-gray-400 mt-1.5">Системный код нельзя изменить после создания типа.</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание (опционально)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm resize-none"
                  placeholder="Краткое описание назначения этого типа..."
                />
              </div>

              {/* Блок с кастомным чекбоксом */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                <CustomCheckbox
                  id="isActive"
                  checked={isActive}
                  onChange={() => setIsActive(!isActive)}
                  label="Активен (доступен для выбора при создании новых шаблонов)"
                />
              </div>

              <div className="pt-4 flex justify-end border-t border-gray-100">
                <button
                  type="submit"
                  disabled={loading}
                  className={`bg-[#1E61AF] text-white px-6 py-2.5 rounded-lg transition-colors font-medium text-sm flex items-center gap-2 ${
                    loading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-800 shadow-sm'
                  }`}
                >
                  {loading ? 'Сохранение...' : (isEditing ? 'Сохранить изменения' : 'Создать тип')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentTypeForm;